import React, { useMemo } from "react";

import { styled, Avatar, List, Divider, Typography, Box, Button, useTheme, IconButton, Stack } from "@mui/material";
import Drawer from "@mui/material/Drawer";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";

import SidebarUser from "./SidebarUser/sidebar-user";
import { useLocation } from "react-router";
import HelpPopup from "../HelpPopup";
import SidebarBrand from "./SidebarBrand/sidebar-brand";
import { TutorialContext } from "../../Utils/Helpers/tutorial";
import { useDispatch } from "react-redux";
import LeftMenu from "./left-menu";
import GlobalConst from "../../Core/Utils/Constants/globals";
import { hexToRgbString } from "../../Utils/Helpers/cssUtils";
import { useTranslation } from "react-i18next";

export const SIDEBAR_WIDTH: number = 180;
export const SIDEBAR_WIDTH_CLOSED: number = 48;

export const SidebarDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== "open" })(({ theme, open }) => ({
  height: "100vh",
  overflow: "hidden",
  padding: `${theme.spacing(1)} ${theme.spacing(0.5)}`,

  "& .MuiDrawer-paper": {
    position: "relative",
    whiteSpace: "nowrap",
    background: "transparent",
    border: "none",
    width: SIDEBAR_WIDTH,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflow: "visible",
    boxSizing: "border-box",
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: SIDEBAR_WIDTH_CLOSED,
      ".MuiChip-root": {
        position: "absolute",
        right: 0,
        top: 0,
      },
    }),
  },
  ".MuiListItemText-root": {
    transition: theme.transitions.create("opacity", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
}));

const SidebarFooter = styled("div")(() => ({
  marginTop: "auto",
}));

interface SidebarProps {
  minified?: boolean;
  open: boolean;
  onUpdate: (open: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onUpdate, minified = false }) => {
  const { t } = useTranslation();

  const sidebarFooterRef = React.useRef<HTMLDivElement>(null);
  const [sidebarFooterHeight, setSidebarFooterHeight] = React.useState<number>(0);

  const [helpPopupAnchorEl, setHelpPopupAnchorEl] = React.useState<HTMLElement | null>(
    (<></>) as unknown as HTMLElement
  );
  const [showHelpPopup, setShowHelpPopup] = React.useState<boolean>(false);

  const tutorialContext = React.useContext(TutorialContext);
  const { setTutorialPage } = { ...tutorialContext };
  const theme = useTheme();

  const menuLeftMemo = useMemo(() => <LeftMenu open={open} />, [open]);

  const handleHelpClick: React.MouseEventHandler<HTMLElement> = (e) => {
    e.stopPropagation();
    if (!setTutorialPage) {
      return;
    }

    setTutorialPage("demo");
    setShowHelpPopup(true);
    setHelpPopupAnchorEl(e.currentTarget);
  };

  const onClosePopup = () => {
    setHelpPopupAnchorEl(null);
  };

  React.useEffect(() => {
    if (!setTutorialPage) {
      return;
    }

    setTutorialPage("demo");
  }, [setTutorialPage]);

  React.useEffect(() => {
    setSidebarFooterHeight(sidebarFooterRef.current?.clientHeight || 0);
  }, [open]);

  return (
    <SidebarDrawer variant="permanent" open={open}>
      <SidebarBrand minified={minified} drawerOpen={open} />

      {!minified && (
        <List
          component="nav"
          sx={{
            pt: 0,
            width: open ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_CLOSED,
            overflowY: "auto !important",
            overflowX: "hidden !important",
            height: "100vh",
            position: "relative",
            marginTop: `calc(${GlobalConst.HEADER_HEIGHT}px - 4px)`,
            marginBottom: `${sidebarFooterHeight}px`,
            ...theme.helpers(theme).scrollBar,
            //scrollbarWidth: "none",
          }}
        >
          {menuLeftMemo}
        </List>
      )}

      <SidebarFooter
        sx={{
          position: "fixed",
          bottom: 0,
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          width: open ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_CLOSED,
        }}
        ref={sidebarFooterRef}
      >
        <Stack px={1} pb={1.5} pt={1} justifyContent="center" alignItems="center" position="relative">
          {open ? (
            <Button
              size="small"
              startIcon={<HeadsetMicOutlinedIcon sx={{ color: "info.main", width: 16, height: 16 }} />}
              onClick={handleHelpClick}
              fullWidth
            >
              {t("common:helpCenter")}
            </Button>
          ) : (
            <IconButton onClick={handleHelpClick} size="small" color="neutral">
              <HeadsetMicOutlinedIcon sx={{ color: "info.main" }} />
            </IconButton>
          )}
        </Stack>
        {helpPopupAnchorEl && (
          <HelpPopup
            visible={Boolean(helpPopupAnchorEl) && showHelpPopup}
            onClose={onClosePopup}
            anchorEl={helpPopupAnchorEl}
          />
        )}

        <Divider />
        <SidebarUser onUpdate={onUpdate} drawerOpen={open} />
      </SidebarFooter>
    </SidebarDrawer>
  );
};

export default Sidebar;
